import type { JsonObject } from './json.ts';
import { z } from 'zod';
import { jsonObjectSchema } from './json.ts';

export const blockSourceSchema = z.object({
  type: z.enum(['catalog', 'generated']),
  artifactId: z.string().min(1).optional(),
}).strict();

export const dataBindingSchema = z.object({
  input: z.string().min(1),
  datasetId: z.string().min(1),
  field: z.string().min(1).optional(),
  fields: z.array(z.string().min(1)).max(1000).default([]),
  limit: z.number().int().positive().max(100000).optional(),
  order: z.enum(['source', 'ascending', 'descending']).default('source'),
}).strict().superRefine((binding, context) => {
  if (binding.field && binding.fields.length > 0) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['fields'],
      message: 'field 与 fields 不能同时配置',
    });
  }
  const fields = new Set<string>();
  for (const [index, field] of binding.fields.entries()) {
    if (fields.has(field)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fields', index],
        message: `绑定字段重复：${field}`,
      });
    }
    fields.add(field);
  }
});

export const interactionBindingSchema = z.object({
  eventType: z.string().min(1),
  targetBlockIds: z.array(z.string().min(1)).max(100).default([]),
  action: z.string().min(1).optional(),
  runtimeId: z.string().min(1).optional(),
  params: jsonObjectSchema.default({}),
}).strict().superRefine((binding, context) => {
  const targets = new Set<string>();
  for (const [index, target] of binding.targetBlockIds.entries()) {
    if (targets.has(target)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['targetBlockIds', index],
        message: `交互目标重复：${target}`,
      });
    }
    targets.add(target);
  }
});

export const blockPlacementSchema = z.object({
  /**
   * freeform 模式下相对容器宽高的归一化坐标，范围 0..1。
   * grid/stack 模式下不作为作者输入。
   */
  x: z.number().min(0).max(1).optional(),
  y: z.number().min(0).max(1).optional(),
  width: z.number().positive().max(1).optional(),
  height: z.number().positive().max(1).optional(),
  minWidth: z.number().positive().finite().max(1000000).optional(),
  minHeight: z.number().positive().finite().max(1000000).optional(),
  maxWidth: z.number().positive().finite().max(1000000).optional(),
  maxHeight: z.number().positive().finite().max(1000000).optional(),
  zIndex: z.number().int().nonnegative().max(1000000).default(0),
  column: z.number().int().positive().max(1000).optional(),
  row: z.number().int().positive().max(1000000).optional(),
  columnSpan: z.number().int().positive().max(1000).optional(),
  rowSpan: z.number().int().positive().max(1000000).optional(),
}).strict().superRefine((placement, context) => {
  if (
    placement.minWidth !== undefined
    && placement.maxWidth !== undefined
    && placement.minWidth > placement.maxWidth
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['maxWidth'],
      message: '最大宽度不能小于最小宽度',
    });
  }
  if (
    placement.minHeight !== undefined
    && placement.maxHeight !== undefined
    && placement.minHeight > placement.maxHeight
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['maxHeight'],
      message: '最大高度不能小于最小高度',
    });
  }
});

export interface VisualizationBlockSpecShape {
  id: string;
  componentKey: string;
  title: string;
  config: JsonObject;
  bindings: z.infer<typeof dataBindingSchema>[];
  interactions: z.infer<typeof interactionBindingSchema>[];
  source: z.infer<typeof blockSourceSchema>;
  placement: z.infer<typeof blockPlacementSchema>;
  children?: VisualizationBlockSpecShape[];
  childRatios?: number[];
}

// 输入形状：带默认值的字段在输入时可选
export interface VisualizationBlockSpecInputShape {
  id: string;
  componentKey?: string;
  title?: string;
  config?: JsonObject;
  bindings?: z.input<typeof dataBindingSchema>[];
  interactions?: z.input<typeof interactionBindingSchema>[];
  source?: z.input<typeof blockSourceSchema>;
  placement?: z.input<typeof blockPlacementSchema>;
  children?: VisualizationBlockSpecInputShape[];
  childRatios?: number[];
}

export const visualizationBlockSpecSchema: z.ZodType<
  VisualizationBlockSpecShape,
  z.ZodTypeDef,
  VisualizationBlockSpecInputShape
> = z.object({
  id: z.string().min(1).max(200),
  componentKey: z.string().max(200).default(''),
  title: z.string().max(100000).default(''),
  config: jsonObjectSchema.default({}),
  bindings: z.array(dataBindingSchema).max(100).default([]),
  interactions: z.array(interactionBindingSchema).max(100).default([]),
  source: blockSourceSchema.default({ type: 'catalog' }),
  placement: blockPlacementSchema.default({
    zIndex: 0,
  }),
  /** V1 布局容器子节点（如 split-view），最大 20 深度递归 */
  children: z.array(z.lazy(() => visualizationBlockSpecSchema)).max(100).optional(),
  /** 子节点之间共享的占位比例，如 split-view 的 0.5，由容器写回 */
  childRatios: z.array(z.number().min(0).max(1)).max(100).optional(),
}).strict().superRefine((block, context) => {
  const inputs = new Set<string>();
  for (const [index, binding] of block.bindings.entries()) {
    if (inputs.has(binding.input)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['bindings', index, 'input'],
        message: `同一输入不能重复绑定：${binding.input}`,
      });
    }
    inputs.add(binding.input);
  }
});

export type BlockSource = z.infer<typeof blockSourceSchema>;
export type DataBinding = z.infer<typeof dataBindingSchema>;
export type InteractionBinding = z.infer<typeof interactionBindingSchema>;
export type BlockPlacement = z.infer<typeof blockPlacementSchema>;
export type VisualizationBlockSpec = z.infer<
  typeof visualizationBlockSpecSchema
>;
